import React, { useState, useEffect } from "react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { Skeleton } from "src/components/ui/skeleton"
import { useToast } from "src/components/ui/toast"
import { QrCampaignForm } from "./QrCampaignForm"
import { useCreateQrCampaign, useUpdateQrCampaign } from "../hooks/useQrCampaigns"
import { fetchQrCampaign } from "../api/qrCampaignsApi"

export function QrCampaignDrawer({ open, campaignId, onClose, onSuccess }) {
  const isEdit = !!campaignId
  const { toast } = useToast()
  const createHook = useCreateQrCampaign()
  const updateHook = useUpdateQrCampaign()
  const isPending = createHook.isPending || updateHook.isPending

  // The list rows omit `content`/`project_images`; fetch the full record to edit.
  const [full, setFull] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    if (!isEdit) { setFull(null); return }
    setLoading(true)
    fetchQrCampaign(campaignId)
      .then(setFull)
      .catch(() => toast({ variant: "destructive", title: "Error", description: "Could not load campaign" }))
      .finally(() => setLoading(false))
  }, [open, campaignId]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(payload) {
    try {
      if (isEdit) {
        await updateHook.mutate(campaignId, payload)
        toast({ title: "Campaign updated", description: payload.label })
      } else {
        await createHook.mutate(payload)
        toast({ title: "Campaign created", description: payload.label })
      }
      onSuccess?.()
      onClose()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : errors?.message || "Something went wrong"
      toast({ variant: "destructive", title: "Error", description: msg })
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[860px]">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit" : "New"} QR campaign</SheetTitle>
          <SheetDescription>
            A public micro-site on its own subdomain that captures leads from an enquiry form.
          </SheetDescription>
        </SheetHeader>

        {isEdit && loading ? (
          <div className="space-y-3 p-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <QrCampaignForm
            campaign={isEdit ? full : null}
            onSubmit={handleSubmit}
            onCancel={onClose}
            isPending={isPending}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}
